import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  LinearProgress,
  IconButton,
  MenuItem,
  Paper,
  Chip,
  Stack,
  TextField,
  Typography,
  Fade,
  Alert,
  InputAdornment
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DescriptionIcon from '@mui/icons-material/Description';
import EventIcon from '@mui/icons-material/Event';
import CategoryIcon from '@mui/icons-material/Category';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import FlightLandIcon from '@mui/icons-material/FlightLand';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import CakeIcon from '@mui/icons-material/Cake';
import PhoneIcon from '@mui/icons-material/Phone';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { useNavigate, useParams } from 'react-router-dom';
import { FUND_CATEGORIES, FUND_TYPES, KASBON_CATEGORIES, TRAVEL_PURPOSES, createFundRequest, updateFundRequest, listPayees, uploadFundAttachment, listInstallationTeams, listTravelCities, getFundRequest } from '../../services/fundRequestService';
import api from '../../services/api';
import { toast } from 'react-toastify';

const emptyItem = { description: '', qty: 1, unit: '', unitPrice: 0, category: '' };
const emptyPassenger = { name: '', ktpNumber: '', birthDate: '', phone: '' };
const TYPE_LABELS = {
  GENERAL: 'PENGAJUAN DANA',
  CASH_ADVANCE: 'KASBON',
  TRAVEL: 'PERJALANAN / TIKET'
};
const BANK_OPTIONS = [
  'BANK BRI',
  'BANK MANDIRI',
  'BANK BNI',
  'BANK BTN',
  'BANK BCA',
  'BANK CIMB NIAGA',
  'BANK PERMATA',
  'BANK DANAMON',
  'BANK MEGA',
  'BANK PANIN',
  'BANK OCBC NISP',
  'BANK BTPN',
  'BANK SYARIAH INDONESIA (BSI)',
  'BANK JAGO',
  'BANK NEO COMMERCE',
  'BANK COMMONWEALTH',
  'BANK HSBC',
  'BANK MAYBANK',
  'BANK UOB',
  'BANK MUAMALAT',
  'BANK BJB',
  'BANK JATIM',
  'BANK JATENG',
  'BANK DKI',
  'BANK SUMUT',
  'BANK NAGARI',
  'BANK RIAU KEPRI',
  'BANK PAPUA',
  'BANK SULSELBAR',
  'BANK ACEH',
  'BANK NTB',
  'BANK KALBAR',
  'BANK KALTIMTARA',
  'BANK NTT',
  'BANK MALUKU MALUT',
  'BANK LAMPUNG',
  'BANK BENGKULU',
  'BANK JAMB',
  'BANK DIY',
  'BANK SULTRA',
  'BANK SULUTGO',
  'BANK BPD BALI'
];
const UNIT_OPTIONS = [
  'UNIT',
  'PCS',
  'SET',
  'M',
  'M2',
  'M3',
  'LITER',
  'ML',
  'KG',
  'TON',
  'GR',
  'ROL',
  'LEMBAR',
  'KARUNG',
  'BOX',
  'PACK',
  'HARI',
  'MINGGU',
  'BULAN',
  '% (PERSEN)'
];

const toNumberSafe = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '';
  if (value === '' || value === '-') return String(value);
  const num = Number(value);
  return Number.isFinite(num) ? num.toLocaleString('id-ID') : '';
};

const sanitizeCurrencyInput = (value = '') => {
  const raw = String(value);
  const isNegative = raw.trim().startsWith('-');
  const digits = raw.replace(/[^\d]/g, '');
  if (!digits) return isNegative ? '-' : '';
  return `${isNegative ? '-' : ''}${digits}`;
};
const buildFileUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
  const backendBase = apiBase.replace(/\/api$/, '');
  if (url.startsWith('/api/')) return `${backendBase}${url}`;
  return `${backendBase}/api/uploads/${url.replace(/^\/+/, '')}`;
};

const extractFileName = (rawUrl = '') => {
  if (!rawUrl) return 'lampiran';
  let filePart = rawUrl;
  try {
    const parsed = new URL(rawUrl);
    filePart = parsed.pathname.split('/').pop() || rawUrl;
  } catch (err) {
    filePart = rawUrl;
  }
  const clean = filePart.split('?')[0];
  return decodeURIComponent(clean) || 'lampiran';
};

const buildAirportLabel = (airport) => {
  if (!airport) return '';
  if (typeof airport === 'string') return airport;
  const parts = [airport.name];
  if (airport.cityName) parts.push(airport.cityName);
  const base = parts.filter(Boolean).join(' - ');
  return airport.iataCode ? `${base} (${airport.iataCode})` : base;
};

const formatFileSize = (size) => {
  if (!size && size !== 0) return '';
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  if (size >= 1024) return `${(size / 1024).toFixed(2)} KB`;
  return `${size} B`;
};

const FundRequestForm = () => {
  const navigate = useNavigate();
  const { id: fundRequestId } = useParams();
  const isEditMode = Boolean(fundRequestId);
  const [type, setType] = useState(FUND_TYPES.GENERAL);
  const [projectId, setProjectId] = useState('HO'); // HO/NON-PROJECT DEFAULT
  const [projects, setProjects] = useState([]);
  const [description, setDescription] = useState('');
  const [payee, setPayee] = useState({ payeeId: null, payeeName: '', bankName: '', accountNumber: '', accountHolder: '' });
  const [payeeOptions, setPayeeOptions] = useState([]);
  const [loadingPayees, setLoadingPayees] = useState(false);
  const [items, setItems] = useState([emptyItem]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [cashAdvance, setCashAdvance] = useState({ mandorId: '', cashCategory: '' });
  const [installationTeams, setInstallationTeams] = useState([]);
  
  const [travel, setTravel] = useState({
    departureCityId: null,
    departureCityName: '',
    destinationCityId: null,
    destinationCityName: '',
    departureDate: '',
    departureTime: '',
    returnDate: '',
    returnTime: '',
    purpose: '',
    mandorId: ''
  });
  const [passengers, setPassengers] = useState([emptyPassenger]);
  const [departureAirportOptions, setDepartureAirportOptions] = useState([]);
  const [destinationAirportOptions, setDestinationAirportOptions] = useState([]);
  const [loadingDepartureAirports, setLoadingDepartureAirports] = useState(false);
  const [loadingDestinationAirports, setLoadingDestinationAirports] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [submissionDate, setSubmissionDate] = useState(() => new Date().toISOString().slice(0, 10)); // YYYY-MM-DD today
  const [submissionTime, setSubmissionTime] = useState(() => {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  });
  const [plannedTransferDate, setPlannedTransferDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(isEditMode);

  const fetchProjects = useCallback(async (keyword = '') => {
    try {
      setLoadingProjects(true);
      const res = await api.get('/projects/lookup', { params: { limit: 50, search: keyword || undefined } });
      const projectList = res.data?.projects || res.data?.data || res.data || [];
      setProjects(projectList);
    } catch (err) {
      console.error('FAILED TO LOAD PROJECTS', err);
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects('');
    // prefetch payees
    (async () => {
      setLoadingPayees(true);
      const data = await listPayees('');
      setPayeeOptions(data || []);
      setLoadingPayees(false);
    })();
    // prefetch mandors from InstallationTeams
    (async () => {
      const data = await listInstallationTeams('');
      const parsed = Array.isArray(data) ? data : Array.isArray(data?.installationTeams) ? data.installationTeams : [];
      setInstallationTeams(parsed);
    })();
  }, [fetchProjects, projectId]);

  useEffect(() => {
    if (!isEditMode) return;
    const loadExisting = async () => {
      try {
        setLoadingExisting(true);
        const detailResponse = await getFundRequest(fundRequestId);
        const detail = detailResponse?.data?.data || detailResponse?.data || detailResponse;
        const approvalSteps = detail?.approvalSteps || [];
        const hasApprovedStep = approvalSteps.some(step => step.status === 'approved');
        const editableStatus = detail?.status === 'draft'
          || ((detail?.status === 'submitted' || detail?.status === 'in_review') && !hasApprovedStep);
        if (!detail || !editableStatus) {
          const message = hasApprovedStep
            ? 'Pengajuan ini tidak dapat diedit karena sudah ada approver yang menyetujui.'
            : 'Pengajuan ini tidak dapat diedit karena statusnya tidak mendukung proses edit.';
          toast.error(message);
          navigate('/fund-requests');
          return;
        }
        setType(detail.type || FUND_TYPES.GENERAL);
        if (detail.project?.id) {
          setProjectId(detail.project.id);
          setProjects(prev => {
            if (prev.find(p => String(p.id) === String(detail.project.id))) return prev;
            return [...prev, detail.project];
          });
        } else {
          setProjectId('HO');
        }
        setSubmissionDate((detail.submissionDate || '').slice(0, 10) || new Date().toISOString().slice(0, 10));
        if (detail.submissionTime) {
          setSubmissionTime(detail.submissionTime);
        }
        setPlannedTransferDate((detail.plannedTransferDate || '').slice(0, 10) || new Date().toISOString().slice(0, 10));
        setDescription(detail.description || '');
        setPayee({
          payeeId: detail.payeeId || null,
          payeeName: detail.payeeName || '',
          bankName: detail.bankName || '',
          accountNumber: detail.accountNumber || '',
          accountHolder: detail.accountHolder || ''
        });
        setAttachments((detail.attachments || []).map(att => ({
          url: att.fileUrl,
          name: att.fileName || extractFileName(att.fileUrl),
          type: att.fileType,
          size: att.fileSize
        })));
        setItems(detail.items?.length ? detail.items.map(item => ({
          description: item.description || '',
          qty: Number(item.qty) || 0,
          unit: item.unit || '',
          unitPrice: Number(item.unitPrice) || 0,
          category: item.category || ''
        })) : [{ ...emptyItem }]);
        if (detail.type === FUND_TYPES.CASH_ADVANCE && detail.cashAdvance) {
          setCashAdvance({
            mandorId: detail.cashAdvance.mandorId || '',
            cashCategory: detail.cashAdvance.cashCategory || '',
            amount: Number(detail.totalAmount || 0)
          });
          if (detail.cashAdvance.mandorId && !(installationTeams || []).some(m => String(m.id) === String(detail.cashAdvance.mandorId))) {
            setInstallationTeams(prev => [...prev, { id: detail.cashAdvance.mandorId, name: detail.cashAdvance.mandorNameManual || `Mandor ${detail.cashAdvance.mandorId}` }]);
          }
        }
        if (detail.type === FUND_TYPES.TRAVEL && detail.travelDetail) {
          const td = detail.travelDetail;
          setTravel({
            departureCityId: td.departureCityId || null,
            departureCityName: td.departureCityName || '',
            destinationCityId: td.destinationCityId || null,
            destinationCityName: td.destinationCityName || '',
            departureDate: td.departureDate || '',
            departureTime: td.departureTime || '',
            returnDate: td.returnDate || '',
            returnTime: td.returnTime || '',
            purpose: td.purpose || '',
            mandorId: td.mandorId || ''
          });
          if (td.mandorId && !(installationTeams || []).some(m => String(m.id) === String(td.mandorId))) {
            setInstallationTeams(prev => [...prev, { id: td.mandorId, name: td.mandorNameManual || `Mandor ${td.mandorId}` }]);
          }
          setPassengers(td.passengers?.length ? td.passengers.map(p => ({
            name: p.name || '',
            ktpNumber: p.ktpNumber || '',
            birthDate: p.birthDate || '',
            phone: p.phone || ''
          })) : [{ ...emptyPassenger }]);
        }
      } catch (error) {
        console.error('FAILED TO LOAD FUND REQUEST', error);
        toast.error('Gagal memuat data pengajuan.');
        navigate('/fund-requests');
      } finally {
        setLoadingExisting(false);
      }
    };
    loadExisting();
  }, [isEditMode, fundRequestId, navigate, installationTeams]);

  const handleAddItem = () => setItems(prev => [...prev, { ...emptyItem }]);
  const handleRemoveItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const handleAddPassenger = () => setPassengers(prev => [...prev, { ...emptyPassenger }]);
  const handleRemovePassenger = (idx) => setPassengers(prev => prev.filter((_, i) => i !== idx));

  const handleRemoveAttachment = (idx) => setAttachments(prev => prev.filter((_, i) => i !== idx));

  const validateTravelPassengers = useCallback(() => {
    if (!passengers.length) {
      return 'Minimal satu penumpang harus ditambahkan.';
    }
    for (let i = 0; i < passengers.length; i += 1) {
      const row = `Penumpang ${i + 1}`;
      const passenger = passengers[i];
      if (!passenger.name || !passenger.name.trim()) {
        return `${row}: nama wajib diisi.`;
      }
      if (!/^[a-zA-Z\s.'-]+$/.test(passenger.name.trim())) {
        return `${row}: nama hanya boleh huruf.`;
      }
      if (!passenger.ktpNumber || !/^\d{8,16}$/.test(passenger.ktpNumber)) {
        return `${row}: nomor KTP harus 8-16 digit angka.`;
      }
      if (!passenger.birthDate) {
        return `${row}: tanggal lahir wajib diisi.`;
      }
      if (!passenger.phone || !/^\d{8,15}$/.test(passenger.phone)) {
        return `${row}: nomor HP harus 8-15 digit angka.`;
      }
    }
    return null;
  }, [passengers]);

  const fetchAirports = useCallback(async (keyword = '', type = 'departure') => {
    const setOptions = type === 'departure' ? setDepartureAirportOptions : setDestinationAirportOptions;
    const setLoading = type === 'departure' ? setLoadingDepartureAirports : setLoadingDestinationAirports;
    try {
      setLoading(true);
      const data = await listTravelCities(keyword);
      setOptions(data || []);
    } catch (error) {
      console.error('FAILED TO LOAD AIRPORTS', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAirports('', 'departure');
    fetchAirports('', 'destination');
  }, [fetchAirports]);

  const handleAirportSelection = (field, value) => {
    if (typeof value === 'string') {
      setTravel(prev => ({
        ...prev,
        [`${field}CityName`]: value,
        [`${field}CityId`]: null
      }));
      return;
    }
    if (value) {
      const label = buildAirportLabel(value);
      setTravel(prev => ({
        ...prev,
        [`${field}CityName`]: label,
        [`${field}CityId`]: value.id || null
      }));
    } else {
      setTravel(prev => ({
        ...prev,
        [`${field}CityName`]: '',
        [`${field}CityId`]: null
      }));
    }
  };

  const totalAmount = useMemo(() => {
    if (type === FUND_TYPES.CASH_ADVANCE) {
      return Number(cashAdvance.amount || 0);
    }
    return items.reduce((sum, it) => {
      const qty = toNumberSafe(it.qty);
      const price = toNumberSafe(it.unitPrice);
      return sum + qty * price;
    }, 0);
  }, [items, cashAdvance, type]);

  const primaryCategory = useMemo(() => {
    if (type !== FUND_TYPES.GENERAL) return '';
    return items.find(it => it.category)?.category || '';
  }, [type, items]);

  const itemGridTemplate = useMemo(() => (
    type === FUND_TYPES.GENERAL
      ? { xs: '1fr', sm: '3fr 1fr 1fr 1fr 1fr 2fr auto' }
      : { xs: '1fr', sm: '3fr 1fr 1fr 1fr 2fr auto' }
  ), [type]);

  const validateItems = () => {
    if (type === FUND_TYPES.TRAVEL) return null;
    if (type === FUND_TYPES.CASH_ADVANCE) return null;
    if (!items.length) return 'Minimal 1 baris item wajib diisi.';
    for (let i = 0; i < items.length; i += 1) {
      const row = items[i];
      if (!(row.description || '').trim()) return 'Deskripsi pada setiap baris item wajib diisi.';
      const qty = Number(row.qty);
        if (!Number.isFinite(qty) || qty <= 0) return 'Qty pada setiap baris item wajib diisi dan lebih dari 0.';
        if (type === FUND_TYPES.GENERAL && !(row.category || '').trim()) return 'Kategori pengajuan wajib dipilih pada setiap item.';
      }
      return null;
    };

  const validatePayeeAndAttachments = () => {
    if (type !== FUND_TYPES.TRAVEL) {
      const requiredPayeeFields = [
        { key: 'payeeName', label: 'Nama penerima' },
        { key: 'bankName', label: 'Bank' },
        { key: 'accountNumber', label: 'No rekening' },
        { key: 'accountHolder', label: 'Nama pemilik rekening' }
      ];
      for (const field of requiredPayeeFields) {
        if (!(payee[field.key] || '').trim()) {
          return `${field.label} wajib diisi.`;
        }
      }
    }
    const uploaded = attachments.filter(att => att?.url);
    if (!uploaded.length) return 'Minimal 1 lampiran wajib diupload.';
    return null;
  };

  const buildPayload = (submit) => {
    const inferredCategory = type === FUND_TYPES.GENERAL
      ? (items.find(it => it.category)?.category || null)
      : null;
    const base = {
      type,
      projectId: projectId && projectId !== 'HO' ? Number(projectId) : null,
      category: inferredCategory,
      description,
      submissionDate,
      submissionTime,
      plannedTransferDate,
      payeeId: payee.payeeId || null,
      payeeName: payee.payeeName,
      bankName: payee.bankName,
      accountNumber: payee.accountNumber,
      accountHolder: payee.accountHolder,
      attachments: attachments
        .filter(att => att?.url)
        .map(att => ({
          url: att.url,
          name: att.name,
          type: att.type,
          size: att.size
        })),
      submit
    };

    if (type === FUND_TYPES.GENERAL) {
      return { ...base, items };
    }
    if (type === FUND_TYPES.CASH_ADVANCE) {
      if (!cashAdvance.mandorId) {
        toast.error('Silakan pilih Nama Mandor.');
        return null;
      }
      return { ...base, cashAdvance: { ...cashAdvance, mandorId: Number(cashAdvance.mandorId) || null, mandorNameManual: '' }, amount: totalAmount || 0 };
    }
    if (type === FUND_TYPES.TRAVEL) {
      return {
        ...base,
        travel: {
          ...travel,
          mandorId: travel.mandorId ? Number(travel.mandorId) || null : null,
          mandorNameManual: ''
        },
        items,
        passengers
      };
    }
    return base;
  };

  const onSubmit = async (submit) => {
    try {
      if (type === FUND_TYPES.TRAVEL) {
        const passengerError = validateTravelPassengers();
        if (passengerError) {
          toast.error(passengerError);
          return;
        }
      }
      const itemError = validateItems();
      if (itemError) {
        toast.error(itemError);
        return;
      }
      const payeeError = validatePayeeAndAttachments();
      if (payeeError) {
        toast.error(payeeError);
        return;
      }
      if (description && description.length > 255) {
        toast.error('Alasan / Keterangan Tambahan maksimal 255 karakter.');
        return;
      }
      setSubmitting(true);
      const payload = buildPayload(submit);
      if (!payload) {
        setSubmitting(false);
        return;
      }
      if (isEditMode) {
        await updateFundRequest(fundRequestId, payload);
      } else {
        await createFundRequest(payload);
      }
      toast.success(submit ? 'Pengajuan berhasil dikirim.' : 'Draft berhasil disimpan.');
      navigate('/fund-requests');
    } catch (err) {
      console.error('FAILED TO SUBMIT FUND REQUEST', err);
      toast.error(err.response?.data?.message || err.message || 'GAGAL MENYIMPAN');
    } finally {
      setSubmitting(false);
    }
  };

  const showCashAdvance = type === FUND_TYPES.CASH_ADVANCE;
  const showTravel = type === FUND_TYPES.TRAVEL;
  const cardSx = { borderRadius: 3, borderColor: 'rgba(15, 23, 42, 0.08)', bgcolor: '#FFFFFF', boxShadow: '0 2px 12px rgba(15, 23, 42, 0.06)' };
  const cardHeaderProps = {
    titleTypographyProps: { fontWeight: 800, color: '#0f172a', fontSize: '1.1rem' },
    subheaderTypographyProps: { color: '#64748b', fontSize: '0.875rem' },
    sx: { pb: 1.5, borderBottom: '1px solid rgba(15, 23, 42, 0.06)' }
  };
  const hasAttachment = attachments.some(att => att?.url);
  const isPayeeComplete = Boolean(
    (payee.payeeName || '').trim()
    && (payee.bankName || '').trim()
    && (payee.accountNumber || '').trim()
    && (payee.accountHolder || '').trim()
  );
  const isTravelRouteComplete = Boolean(
    (travel.departureCityName || '').trim()
    && (travel.destinationCityName || '').trim()
    && travel.departureDate
    && travel.departureTime
    && travel.purpose
  );
  const mandatoryChecklist = [
    { label: 'Tipe pengajuan dipilih', done: Boolean(type) },
    { label: 'Project / HO dipilih', done: Boolean(projectId) },
    { label: 'Tanggal pencairan diisi', done: Boolean(plannedTransferDate) },
    { label: 'Lampiran minimal 1 file', done: hasAttachment }
  ];

  if (type === FUND_TYPES.GENERAL) {
    mandatoryChecklist.push(
      { label: 'Detail item lengkap', done: !validateItems() },
      { label: 'Tujuan transfer lengkap', done: isPayeeComplete }
    );
  }

  if (type === FUND_TYPES.CASH_ADVANCE) {
    mandatoryChecklist.push(
      { label: 'Mandor dipilih', done: Boolean(cashAdvance.mandorId) },
      { label: 'Nominal kasbon valid', done: Number(cashAdvance.amount || 0) > 0 },
      { label: 'Tujuan transfer lengkap', done: isPayeeComplete }
    );
  }

  if (type === FUND_TYPES.TRAVEL) {
    mandatoryChecklist.push(
      { label: 'Rute & jadwal perjalanan lengkap', done: isTravelRouteComplete },
      { label: 'Data penumpang valid', done: !validateTravelPassengers() }
    );
  }

  const completedChecklistCount = mandatoryChecklist.filter(item => item.done).length;
  const completionPercent = mandatoryChecklist.length
    ? Math.round((completedChecklistCount / mandatoryChecklist.length) * 100)
    : 0;
  const readinessLabel = completionPercent === 100 ? 'SIAP SUBMIT' : 'PERLU DILENGKAPI';
  const readinessColor = completionPercent === 100 ? 'success' : completionPercent >= 60 ? 'warning' : 'error';
  const selectedProjectLabel = projectId === 'HO'
    ? 'HO / Non Project'
    : (projects.find(p => String(p.id) === String(projectId))?.name || projectId || '-');

  if (isEditMode && loadingExisting) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', pb: 4 }}>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2.5, sm: 3 },
          mb: 3,
          borderRadius: 3,
          borderColor: '#dbeafe',
          background: 'linear-gradient(135deg, #f8fbff 0%, #eef4ff 100%)',
          boxShadow: '0 4px 20px rgba(15, 23, 42, 0.08)',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Box 
                sx={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: 2, 
                  bgcolor: 'rgba(37, 99, 235, 0.1)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}
              >
                <InfoIcon sx={{ color: '#2563eb', fontSize: 28 }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                {isEditMode ? 'EDIT PENGAJUAN DANA' : 'BUAT PENGAJUAN DANA'}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
              Lengkapi detail pengajuan, lampiran, dan data penerima agar proses persetujuan lebih cepat.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
              <Chip
                size="small"
                label={`TIPE: ${TYPE_LABELS[type] || type}`}
                variant="outlined"
                color="primary"
                sx={{ fontWeight: 700, borderRadius: 1.5, px: 0.5 }}
              />
              <Chip
                size="small"
                label={`PROJECT: ${selectedProjectLabel}`}
                variant="outlined"
                sx={{ fontWeight: 700, borderRadius: 1.5, px: 0.5, maxWidth: 280 }}
              />
              <Chip
                size="small"
                label={`LAMPIRAN: ${attachments.length} FILE`}
                variant="outlined"
                color={hasAttachment ? 'success' : 'default'}
                sx={{ fontWeight: 700, borderRadius: 1.5, px: 0.5 }}
              />
            </Stack>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={() => onSubmit(false)}
              disabled={submitting}
              sx={{
                px: 2.5,
                py: 1.2,
                borderRadius: 2,
                fontWeight: 600,
                '&:hover': {
                  bgcolor: 'rgba(15, 23, 42, 0.05)',
                }
              }}
            >
              SIMPAN DRAFT
            </Button>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={() => onSubmit(true)}
              disabled={submitting || !agreeTerms}
              sx={{
                px: 2.5,
                py: 1.2,
                borderRadius: 2,
                fontWeight: 600,
                bgcolor: '#0f172a',
                '&:hover': {
                  bgcolor: '#0b1324',
                }
              }}
            >
              SUBMIT
            </Button>
          </Stack>
        </Stack>
        <Box sx={{ mt: 2.5, p: 2, borderRadius: 2, bgcolor: 'rgba(255, 255, 255, 0.6)', border: '1px solid rgba(15, 23, 42, 0.06)' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                KELENGKAPAN FORM WAJIB
              </Typography>
              {completionPercent === 100 && (
                <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 16 }} />
              )}
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
              {completedChecklistCount}/{mandatoryChecklist.length} • {completionPercent}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={completionPercent}
            color={readinessColor}
            sx={{ height: 10, borderRadius: 999, bgcolor: 'rgba(15, 23, 42, 0.06)' }}
          />
        </Box>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Card variant="outlined" sx={cardSx}>
            <CardHeader
              title="INFORMASI UTAMA"
              subheader="Tentukan tipe pengajuan, proyek, dan tanggal pencairan."
              {...cardHeaderProps}
            />
            <CardContent sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                {/* Basic Information Section */}
                <Grid item xs={12}>
                  <Box sx={{ 
                    p: 2.5, 
                    borderRadius: 2, 
                    bgcolor: 'rgba(15, 23, 42, 0.02)', 
                    border: '1px solid rgba(15, 23, 42, 0.06)',
                    mb: 2 
                  }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CategoryIcon sx={{ fontSize: 20, color: '#2563eb' }} />
                      KATEGORI PENGAJUAN
                    </Typography>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} md={6}>
                        <TextField 
                          fullWidth 
                          select 
                          label="TIPE PENGAJUAN" 
                          value={type} 
                          onChange={e => setType(e.target.value)} 
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <CategoryIcon sx={{ color: '#64748b', fontSize: 20 }} />
                              </InputAdornment>
                            )
                          }}
                        >
                          <MenuItem value={FUND_TYPES.GENERAL}>DANA UMUM</MenuItem>
                          <MenuItem value={FUND_TYPES.CASH_ADVANCE}>KASBON / UPAH KERJA</MenuItem>
                          <MenuItem value={FUND_TYPES.TRAVEL}>TIKET / MOBILISASI</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          loading={loadingProjects}
                          options={['HO', ...projects]}
                          getOptionLabel={(option) => {
                            if (option === 'HO') return 'HO / NON PROJECT';
                            const kode = option.kodeProyek ? `(${option.kodeProyek})` : '';
                            return `${option.name || option.title || 'PROJECT'} ${kode}`.trim();
                          }}
                          value={
                            projectId === 'HO'
                              ? 'HO'
                              : projects.find(p => String(p.id) === String(projectId)) || null
                          }
                          onChange={(_, val) => {
                            if (val === 'HO') {
                              setProjectId('HO');
                            } else {
                              setProjectId(val?.id ? val.id : '');
                            }
                          }}
                          onInputChange={(_, val, reason) => {
                            if (reason === 'input') {
                              fetchProjects(val);
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="PROJECT"
                              helperText="Pilih proyek atau HO untuk non-project"
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                              InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <AccountBalanceIcon sx={{ color: '#64748b', fontSize: 20 }} />
                              </InputAdornment>
                            )
                          }}
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props} key={option === 'HO' ? 'HO' : option.id}>
                              {option === 'HO'
                                ? 'HO / NON PROJECT'
                                : `${option.name || option.title || 'PROJECT'}${option.kodeProyek ? ` (${option.kodeProyek})` : ''}`}
                            </li>
                          )}
                          blurOnSelect
                          freeSolo={false}
                          fullWidth
                          selectOnFocus
                          clearOnBlur
                          handleHomeEndKeys
                          noOptionsText={loadingProjects ? 'LOADING...' : 'TIDAK ADA PROYEK'}
                          sx={{ width: '100%' }}
                          disableClearable={false}
                          isOptionEqualToValue={(opt, val) => opt === val || String(opt.id) === String(val.id)}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>

                {/* Schedule Section */}
                <Grid item xs={12}>
                  <Box sx={{ 
                    p: 2.5, 
                    borderRadius: 2, 
                    bgcolor: 'rgba(15, 23, 42, 0.02)', 
                    border: '1px solid rgba(15, 23, 42, 0.06)',
                    mb: 2 
                  }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EventIcon sx={{ fontSize: 20, color: '#2563eb' }} />
                      JADWAL PENCAIRAN
                    </Typography>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          type="date"
                          label="TANGGAL PENCAIRAN"
                          InputLabelProps={{ shrink: true }}
                          value={plannedTransferDate}
                          onChange={e => setPlannedTransferDate(e.target.value)}
                          helperText="Tanggal dana ingin dicairkan"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <EventIcon sx={{ color: '#64748b', fontSize: 20 }} />
                              </InputAdornment>
                            )
                          }}
                        />
                      </Grid>
                      {type === FUND_TYPES.TRAVEL && (
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            select
                            label="TUJUAN KEBERANGKATAN"
                            value={travel.purpose}
                            onChange={e => setTravel(prev => ({ ...prev, purpose: e.target.value }))}
                            helperText="Pilih jenis perjalanan"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <DescriptionIcon sx={{ color: '#64748b', fontSize: 20 }} />
                                </InputAdornment>
                              )
                            }}
                          >
                            {TRAVEL_PURPOSES.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                          </TextField>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Grid>

                {/* Additional Information Section */}
                {type === FUND_TYPES.GENERAL && (
                  <Grid item xs={12}>
                    <Box sx={{ 
                      p: 2.5, 
                      borderRadius: 2, 
                      bgcolor: 'rgba(15, 23, 42, 0.02)', 
                      border: '1px solid rgba(15, 23, 42, 0.06)' 
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DescriptionIcon sx={{ fontSize: 20, color: '#2563eb' }} />
                        KETERANGAN TAMBAHAN
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="ALASAN / KETERANGAN"
                        placeholder="Jelaskan alasan permintaan dana"
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        inputProps={{ maxLength: 255 }}
                        helperText={`${description.length}/255 karakter (Opsional)`}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {!showCashAdvance && (
          <Card variant="outlined" sx={{ mt: 2, ...cardSx }}>
            <CardHeader
              title={showTravel ? 'DETAIL (TRAVEL)' : 'DETAIL ITEM'}
              subheader={showTravel ? 'Isi rute dan jadwal perjalanan.' : 'Tambahkan rincian item pengajuan.'}
              {...cardHeaderProps}
            />
            <CardContent sx={{ pt: 2 }}>
              {showTravel && (
                <Grid container spacing={3}>
                  {/* Route Information Section */}
                  <Grid item xs={12}>
                    <Box sx={{ 
                      p: 2.5, 
                      borderRadius: 2, 
                      bgcolor: 'rgba(15, 23, 42, 0.02)', 
                      border: '1px solid rgba(15, 23, 42, 0.06)',
                      mb: 3 
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FlightTakeoffIcon sx={{ fontSize: 20, color: '#2563eb' }} />
                        RUTE PERJALANAN
                      </Typography>
                      <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={6}>
                          <Autocomplete
                            fullWidth
                            freeSolo
                            loading={loadingDepartureAirports}
                            options={departureAirportOptions}
                            getOptionLabel={(option) => buildAirportLabel(option)}
                            value={travel.departureCityName || ''}
                            onChange={(_, val) => handleAirportSelection('departure', val)}
                            onInputChange={(_, val, reason) => {
                              if (reason === 'input') {
                                setTravel(prev => ({ ...prev, departureCityName: val, departureCityId: null }));
                                fetchAirports(val, 'departure');
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="BANDARA KEBERANGKATAN"
                                helperText="Cari bandara atau kota keberangkatan"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <FlightTakeoffIcon sx={{ color: '#64748b', fontSize: 20 }} />
                                    </InputAdornment>
                                  )
                                }}
                              />
                            )}
                            noOptionsText={loadingDepartureAirports ? 'LOADING...' : 'TIDAK ADA BANDARA'}
                            isOptionEqualToValue={(option, value) => {
                              if (typeof value === 'string') return buildAirportLabel(option).toLowerCase() === value.toLowerCase();
                              return option.id === value.id;
                            }}
                            renderOption={(props, option) => (
                              <li {...props} key={option.id}>
                                {buildAirportLabel(option)}
                              </li>
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Autocomplete
                            fullWidth
                            freeSolo
                            loading={loadingDestinationAirports}
                            options={destinationAirportOptions}
                            getOptionLabel={(option) => buildAirportLabel(option)}
                            value={travel.destinationCityName || ''}
                            onChange={(_, val) => handleAirportSelection('destination', val)}
                            onInputChange={(_, val, reason) => {
                              if (reason === 'input') {
                                setTravel(prev => ({ ...prev, destinationCityName: val, destinationCityId: null }));
                                fetchAirports(val, 'destination');
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="BANDARA TUJUAN"
                                helperText="Cari bandara atau kota tujuan"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <FlightLandIcon sx={{ color: '#64748b', fontSize: 20 }} />
                                    </InputAdornment>
                                  )
                                }}
                              />
                            )}
                            noOptionsText={loadingDestinationAirports ? 'LOADING...' : 'TIDAK ADA BANDARA'}
                            isOptionEqualToValue={(option, value) => {
                              if (typeof value === 'string') return buildAirportLabel(option).toLowerCase() === value.toLowerCase();
                              return option.id === value.id;
                            }}
                            renderOption={(props, option) => (
                              <li {...props} key={option.id}>
                                {buildAirportLabel(option)}
                              </li>
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* Departure Schedule Section */}
                  <Grid item xs={12}>
                    <Box sx={{ 
                      p: 2.5, 
                      borderRadius: 2, 
                      bgcolor: 'rgba(15, 23, 42, 0.02)', 
                      border: '1px solid rgba(15, 23, 42, 0.06)',
                      mb: 3 
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EventIcon sx={{ fontSize: 20, color: '#2563eb' }} />
                        JADWAL KEBERANGKATAN
                      </Typography>
                      <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={6}>
                          <TextField 
                            fullWidth 
                            type="date" 
                            InputLabelProps={{ shrink: true }} 
                            label="TANGGAL KEBERANGKATAN" 
                            value={travel.departureDate} 
                            onChange={e => setTravel(prev => ({ ...prev, departureDate: e.target.value }))}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <EventIcon sx={{ color: '#64748b', fontSize: 20 }} />
                                </InputAdornment>
                              )
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField 
                            fullWidth 
                            type="time" 
                            InputLabelProps={{ shrink: true }} 
                            label="JAM KEBERANGKATAN" 
                            value={travel.departureTime} 
                            onChange={e => setTravel(prev => ({ ...prev, departureTime: e.target.value }))}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <EventIcon sx={{ color: '#64748b', fontSize: 20 }} />
                                </InputAdornment>
                              )
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* Return Schedule Section */}
                  <Grid item xs={12}>
                    <Box sx={{ 
                      p: 2.5, 
                      borderRadius: 2, 
                      bgcolor: 'rgba(15, 23, 42, 0.02)', 
                      border: '1px dashed rgba(15, 23, 42, 0.15)'
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#64748b', mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EventIcon sx={{ fontSize: 20, color: '#94a3b8' }} />
                        JADWAL PULANG (OPSIONAL)
                      </Typography>
                      <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={6}>
                          <TextField 
                            fullWidth 
                            type="date" 
                            InputLabelProps={{ shrink: true }} 
                            label="TANGGAL PULANG" 
                            value={travel.returnDate} 
                            onChange={e => setTravel(prev => ({ ...prev, returnDate: e.target.value }))}
                            helperText="Boleh diisi nanti"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <EventIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                                </InputAdornment>
                              )
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField 
                            fullWidth 
                            type="time" 
                            InputLabelProps={{ shrink: true }} 
                            label="JAM PULANG" 
                            value={travel.returnTime} 
                            onChange={e => setTravel(prev => ({ ...prev, returnTime: e.target.value }))}
                            helperText="Boleh diisi nanti"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <EventIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                                </InputAdornment>
                              )
                            }}
                          />
                        </Grid>
                      </Grid>
                      <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                        Jadwal kepulangan tidak wajib diisi saat ini, dapat dilengkapi kemudian.
                      </Alert>
                    </Box>
                  </Grid>
                </Grid>
              )}

            {!showTravel && (
              <>
                <Box
                  sx={{
                    display: { xs: 'none', sm: 'grid' },
                    gap: 1,
                    gridTemplateColumns: itemGridTemplate,
                    alignItems: 'center',
                    px: 1.5,
                    py: 1,
                    mb: 1.2,
                    borderRadius: 1.5,
                    bgcolor: '#f8fafc',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary' }}>DESKRIPSI</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary' }}>QTY</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary' }}>SATUAN</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary' }}>HARGA SATUAN</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary' }}>TOTAL (RP)</Typography>
                  {type === FUND_TYPES.GENERAL && (
                    <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary' }}>KATEGORI</Typography>
                  )}
                  <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textAlign: 'center' }}>AKSI</Typography>
                </Box>
                {items.map((item, idx) => (
                  <Paper
                    variant="outlined"
                    key={idx}
                    sx={{
                      mb: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      borderColor: '#e5e7eb',
                      bgcolor: '#ffffff',
                      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
                      }
                    }}
                  >
                    <Chip
                      size="small"
                      label={`ITEM ${idx + 1}`}
                      sx={{
                        mb: 1,
                        fontWeight: 700,
                        bgcolor: '#eff6ff',
                        color: '#1e3a8a'
                      }}
                    />
                    <Box
                      sx={{
                        display: 'grid',
                        gap: 1,
                        gridTemplateColumns: itemGridTemplate,
                        alignItems: 'center'
                      }}
                    >
                      <TextField required label="DESKRIPSI" value={item.description} onChange={e => setItems(prev => prev.map((it, i) => i === idx ? { ...it, description: e.target.value } : it))} />
                      <TextField required label="QTY" type="number" value={item.qty} onChange={e => setItems(prev => prev.map((it, i) => i === idx ? { ...it, qty: e.target.value } : it))} />
                      <Autocomplete
                        freeSolo
                        options={UNIT_OPTIONS}
                        value={item.unit || ''}
                        onChange={(_, val) => {
                          setItems(prev => prev.map((it, i) => i === idx ? { ...it, unit: val || '' } : it));
                        }}
                        onInputChange={(_, val, reason) => {
                          if (reason === 'input') {
                            setItems(prev => prev.map((it, i) => i === idx ? { ...it, unit: val } : it));
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="SATUAN"
                          />
                        )}
                      />
                      <TextField
                        label="HARGA SATUAN"
                        value={formatCurrency(item.unitPrice)}
                        onChange={e => {
                          const sanitized = sanitizeCurrencyInput(e.target.value);
                          setItems(prev => prev.map((it, i) => i === idx ? { ...it, unitPrice: sanitized } : it));
                        }}
                        InputProps={{ sx: { '& input': { color: toNumberSafe(item.unitPrice) < 0 ? 'error.main' : 'inherit' } } }}
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0. }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', px: 0.25 }}>
                          {/* TOTAL (RP) */}
                        </Typography>
                        <Paper
                          variant="outlined"
                          sx={{
                            minHeight: 30,
                            px: 1.5,
                            py: 1.25,
                            display: 'flex',
                            alignItems: 'center',
                            borderStyle: 'dashed',
                            borderColor: '#cbd5e1',
                            bgcolor: '#f8fafc'
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 700,
                              color: toNumberSafe(item.qty) * toNumberSafe(item.unitPrice) < 0 ? 'error.main' : 'text.primary'
                            }}
                          >
                            {formatCurrency(toNumberSafe(item.qty) * toNumberSafe(item.unitPrice))}
                          </Typography>
                        </Paper>
                      </Box>
                      {type === FUND_TYPES.GENERAL && (
                        <TextField required select label="KATEGORI PENGAJUAN" value={item.category || ''} onChange={e => setItems(prev => prev.map((it, i) => i === idx ? { ...it, category: e.target.value } : it))}>
                          {FUND_CATEGORIES.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                        </TextField>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveItem(idx)}
                          disabled={items.length === 1}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </Paper>
                ))}
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddItem}
                  sx={{
                    alignSelf: 'flex-start',
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: 'rgba(37, 99, 235, 0.05)',
                    }
                  }}
                >
                  TAMBAH BARIS ITEM
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}

          {showCashAdvance && (
            <Card variant="outlined" sx={{ mt: 2, ...cardSx }}>
              <CardHeader
                title="DETAIL KASBON / UPAH KERJA"
                subheader="Masukkan mandor dan nominal kasbon."
                {...cardHeaderProps}
              />
              <CardContent sx={{ pt: 2 }}>
                <Grid container spacing={3}>
                  {/* Mandor Selection Section */}
                  <Grid item xs={12}>
                    <Box sx={{ 
                      p: 2.5, 
                      borderRadius: 2, 
                      bgcolor: 'rgba(15, 23, 42, 0.02)', 
                      border: '1px solid rgba(15, 23, 42, 0.06)',
                      mb: 3 
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ fontSize: 20, color: '#2563eb' }} />
                        PENANGGUNG JAWAB
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        label="NAMA MANDOR"
                        required
                        value={cashAdvance.mandorId}
                        onChange={e => setCashAdvance(prev => ({ ...prev, mandorId: e.target.value }))}
                        helperText="Pilih mandor aktif (dikelola di Installation Teams)"
                        SelectProps={{ native: false }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon sx={{ color: '#64748b', fontSize: 20 }} />
                            </InputAdornment>
                          )
                        }}
                      >
                        {(installationTeams || []).map(team => (
                          <MenuItem key={team.id} value={team.id}>{team.name}</MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  </Grid>

                  {/* Cash Advance Details Section */}
                  <Grid item xs={12}>
                    <Box sx={{ 
                      p: 2.5, 
                      borderRadius: 2, 
                      bgcolor: 'rgba(15, 23, 42, 0.02)', 
                      border: '1px solid rgba(15, 23, 42, 0.06)',
                      mb: 3 
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AttachMoneyIcon sx={{ fontSize: 20, color: '#2563eb' }} />
                        DETAIL KASBON
                      </Typography>
                      <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={6}>
                          <TextField 
                            fullWidth 
                            select 
                            label="KATEGORI KASBON" 
                            value={cashAdvance.cashCategory} 
                            onChange={e => setCashAdvance(prev => ({ ...prev, cashCategory: e.target.value }))}
                            helperText="Pilih kategori sesuai jenis pengajuan kasbon"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <CategoryIcon sx={{ color: '#64748b', fontSize: 20 }} />
                                </InputAdornment>
                              )
                            }}
                          >
                            {KASBON_CATEGORIES.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="NOMINAL KASBON"
                            type="text"
                            inputMode="numeric"
                            value={formatCurrency(cashAdvance.amount || '')}
                            onChange={e => {
                              const sanitized = sanitizeCurrencyInput(e.target.value);
                              setCashAdvance(prev => ({ ...prev, amount: sanitized ? Number(sanitized) : '' }));
                            }}
                            helperText="Masukkan nominal dalam Rupiah"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <AttachMoneyIcon sx={{ color: '#64748b', fontSize: 20 }} />
                                </InputAdornment>
                              )
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* Additional Information Section */}
                  <Grid item xs={12}>
                    <Box sx={{ 
                      p: 2.5, 
                      borderRadius: 2, 
                      bgcolor: 'rgba(15, 23, 42, 0.02)', 
                      border: '1px solid rgba(15, 23, 42, 0.06)' 
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DescriptionIcon sx={{ fontSize: 20, color: '#2563eb' }} />
                        KETERANGAN TAMBAHAN
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="ALASAN / KETERANGAN"
                        placeholder="Jelaskan alasan permintaan kasbon"
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        inputProps={{ maxLength: 255 }}
                        helperText={`${description.length}/255 karakter (Opsional)`}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {showTravel && (
            <Card variant="outlined" sx={{ mt: 2, ...cardSx }}>
              <CardHeader
                title="PENUMPANG"
                subheader="Tambahkan data penumpang yang akan diberangkatkan."
                {...cardHeaderProps}
              />
              <CardContent sx={{ pt: 2 }}>
                {/* Mandor Selection */}
                <Box sx={{ 
                  p: 2.5, 
                  borderRadius: 2, 
                  bgcolor: 'rgba(15, 23, 42, 0.02)', 
                  border: '1px solid rgba(15, 23, 42, 0.06)',
                  mb: 3 
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon sx={{ fontSize: 20, color: '#2563eb' }} />
                    PENANGGUNG JAWAB
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    label="NAMA MANDOR"
                    value={travel.mandorId}
                    onChange={e => setTravel(prev => ({ ...prev, mandorId: e.target.value }))}
                    helperText="Opsional: pilih mandor aktif (Installation Teams)"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon sx={{ color: '#64748b', fontSize: 20 }} />
                        </InputAdornment>
                      )
                    }}
                  >
                    {(installationTeams || []).map(team => (
                      <MenuItem key={team.id} value={team.id}>{team.name}</MenuItem>
                    ))}
                  </TextField>
                </Box>

                {/* Passenger List */}
                <Box sx={{ 
                  p: 2.5, 
                  borderRadius: 2, 
                  bgcolor: 'rgba(15, 23, 42, 0.02)', 
                  border: '1px solid rgba(15, 23, 42, 0.06)',
                  mb: 2 
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon sx={{ fontSize: 20, color: '#2563eb' }} />
                    DATA PENUMPANG
                  </Typography>
                  {passengers.map((p, idx) => (
                    <Fade in={true} key={idx}>
                      <Paper
                        variant="outlined"
                        sx={{
                          mb: 2,
                          p: 2,
                          borderRadius: 2,
                          borderColor: '#e5e7eb',
                          bgcolor: '#ffffff',
                          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                          <Chip
                            size="small"
                            label={`PENUMPANG ${idx + 1}`}
                            sx={{
                              fontWeight: 700,
                              bgcolor: '#eff6ff',
                              color: '#1e3a8a',
                              borderRadius: 1.5
                            }}
                          />
                          <IconButton 
                            onClick={() => handleRemovePassenger(idx)} 
                            disabled={passengers.length === 1}
                            sx={{ 
                              color: 'error.main',
                              '&:hover': {
                                bgcolor: 'rgba(239, 68, 68, 0.1)',
                              }
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              required
                              label="NAMA LENGKAP"
                              value={p.name}
                              onChange={e => setPassengers(prev => prev.map((it, i) => i === idx ? { ...it, name: e.target.value } : it))}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <PersonIcon sx={{ color: '#64748b', fontSize: 20 }} />
                                  </InputAdornment>
                                )
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              required
                              label="NO KTP"
                              value={p.ktpNumber}
                              onChange={e => setPassengers(prev => prev.map((it, i) => i === idx ? { ...it, ktpNumber: e.target.value.replace(/\D/g, '') } : it))}
                              inputProps={{ inputMode: 'numeric', maxLength: 16 }}
                              helperText="8-16 digit angka"
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <BadgeIcon sx={{ color: '#64748b', fontSize: 20 }} />
                                  </InputAdornment>
                                )
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              required
                              type="date"
                              InputLabelProps={{ shrink: true }}
                              label="TANGGAL LAHIR"
                              value={p.birthDate}
                              onChange={e => setPassengers(prev => prev.map((it, i) => i === idx ? { ...it, birthDate: e.target.value } : it))}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <CakeIcon sx={{ color: '#64748b', fontSize: 20 }} />
                                  </InputAdornment>
                                )
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              required
                              label="NO HP"
                              value={p.phone}
                              onChange={e => setPassengers(prev => prev.map((it, i) => i === idx ? { ...it, phone: e.target.value.replace(/\D/g, '') } : it))}
                              inputProps={{ inputMode: 'numeric', maxLength: 15 }}
                              helperText="8-15 digit angka"
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <PhoneIcon sx={{ color: '#64748b', fontSize: 20 }} />
                                  </InputAdornment>
                                )
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    </Fade>
                  ))}
                  <Button 
                    variant="contained"
                    startIcon={<AddIcon />} 
                    onClick={handleAddPassenger}
                    sx={{
                      borderRadius: 2,
                      px: 2.5,
                      py: 1.2,
                      fontWeight: 600,
                      mt: 1,
                      '&:hover': {
                        bgcolor: 'rgba(37, 99, 235, 0.9)',
                      }
                    }}
                  >
                    TAMBAH PENUMPANG
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ position: { md: 'sticky' }, top: { md: 84 } }}>
          {type !== FUND_TYPES.TRAVEL && (
            <Card variant="outlined" sx={{ ...cardSx, mb: 4, borderRadius: 2, boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)' }}>
            <CardHeader
              title="TUJUAN TRANSFER"
              subheader="Data rekening tujuan pencairan."
              {...cardHeaderProps}
            />
            <CardContent sx={{ pt: 4, pb: 4 }}>
                <Autocomplete
                loading={loadingPayees}
                options={['__HO_OPTION__', ...payeeOptions]}
                freeSolo
                getOptionLabel={(option) => {
                  if (option === '__HO_OPTION__') return 'Pilih penerima tersimpan...';
                  if (typeof option === 'string') return option;
                  const labelParts = [
                    option.name || '',
                    option.bankName || option.bankname || '',
                    option.accountNumber || option.accountnumber || ''
                  ].filter(Boolean);
                  return labelParts.join(' - ');
                }}
                value={
                  payee.payeeId
                    ? payeeOptions.find(o => o.id === payee.payeeId) || payee.payeeName
                    : payee.payeeName
                }
                onChange={async (_, val) => {
                  if (typeof val === 'string') {
                    setPayee(prev => ({ ...prev, payeeId: null, payeeName: val }));
                  } else if (val && val !== '__HO_OPTION__') {
                    setPayee({
                      payeeId: val.id,
                      payeeName: val.name || '',
                      bankName: val.bankName || val.bankname || '',
                      accountNumber: val.accountNumber || val.accountnumber || '',
                      accountHolder: val.accountHolder || val.accountholder || ''
                    });
                  } else {
                    setPayee({ payeeId: null, payeeName: '', bankName: '', accountNumber: '', accountHolder: '' });
                  }
                }}
                onInputChange={async (_, val, reason) => {
                  if (reason === 'input') {
                    setPayee(prev => ({ ...prev, payeeName: val, payeeId: null }));
                    setLoadingPayees(true);
                    const data = await listPayees(val);
                    setPayeeOptions(data || []);
                    setLoadingPayees(false);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="NAMA PENERIMA / BANK / NO REKENING"
                    required
                    sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    helperText="Pilih dari daftar atau ketik baru untuk menambah penerima (wajib diisi)"
                  />
                )}
                renderOption={(props, option) => {
                  if (option === '__HO_OPTION__') return null;
                  return (
                    <li {...props} key={option.id}>
                      {`${option.name || ''} - ${(option.bankName || option.bankname || '')} (${option.accountNumber || option.accountnumber || ''})`}
                    </li>
                  );
                }}
                blurOnSelect
                fullWidth
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                noOptionsText={loadingPayees ? 'LOADING...' : 'TIDAK ADA PENERIMA'}
                sx={{ width: '100%' }}
                disableClearable={false}
                isOptionEqualToValue={(opt, val) => opt === val || String(opt.id) === String(val.id)}
              />
              <Autocomplete
                freeSolo
                options={BANK_OPTIONS}
                value={payee.bankName || ''}
                onChange={(_, val) => setPayee(prev => ({ ...prev, bankName: val || '' }))}
                onInputChange={(_, val, reason) => {
                  if (reason === 'input') {
                    setPayee(prev => ({ ...prev, bankName: val }));
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="BANK"
                    required
                    sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    helperText="Pilih bank atau ketik nama bank (wajib diisi)"
                  />
                )}
              />
              <TextField fullWidth required label="NO REKENING" sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} value={payee.accountNumber} onChange={e => setPayee(prev => ({ ...prev, accountNumber: e.target.value }))} />
              <TextField fullWidth required label="NAMA PEMILIK REKENING" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} value={payee.accountHolder} onChange={e => setPayee(prev => ({ ...prev, accountHolder: e.target.value }))} />
              </CardContent>
            </Card>
          )}

          <Card variant="outlined" sx={{ ...cardSx, mb: 2 }}>
            <CardHeader
              title="LAMPIRAN"
              subheader="Upload file (PDF/JPG/PNG/Excel/Word). Minimal 1 lampiran."
              {...cardHeaderProps}
            />
            <CardContent sx={{ pt: 2 }}>
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: attachments.length === 0 ? 'error.main' : 'rgba(15, 23, 42, 0.2)',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  bgcolor: 'rgba(15, 23, 42, 0.02)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'rgba(37, 99, 235, 0.03)',
                  }
                }}
              >
                <UploadFileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {uploading ? 'Mengupload file...' : 'Klik untuk memilih file atau drag & drop'}
                </Typography>
                <Button
                  variant="contained"
                  component="label"
                  disabled={uploading}
                  startIcon={<UploadFileIcon />}
                  sx={{
                    borderRadius: 2,
                    px: 2.5,
                    py: 1,
                    fontWeight: 600
                  }}
                >
                  {uploading ? 'UPLOADING...' : 'PILIH FILE'}
                  <input
                    hidden
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        setUploading(true);
                        const uploaded = await uploadFundAttachment(file);
                        setAttachments(prev => [...prev, {
                          url: uploaded.url,
                          name: uploaded.filename || file.name,
                          type: uploaded.mimetype,
                          size: uploaded.size
                        }]);
                        toast.success('File berhasil diupload');
                      } catch (err) {
                        toast.error(err.response?.data?.message || err.message || 'Gagal upload');
                      } finally {
                        setUploading(false);
                        e.target.value = '';
                      }
                    }}
                  />
                </Button>
              </Box>
              {attachments.length === 0 && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                  Minimal 1 lampiran wajib diupload.
                </Alert>
              )}
              <Stack spacing={1}>
                {attachments.map((file, idx) => {
                  if (!file?.url) return null;
                  const fullUrl = buildFileUrl(file.url);
                  const fileName = file.name || extractFileName(file.url);
                  return (
                    <Fade in={true} key={`${file.url}-${idx}`}>
                      <Box 
                        sx={{ 
                          mb: 1.5, 
                          p: 1.5, 
                          borderRadius: 2, 
                          bgcolor: '#f8fafc', 
                          border: '1px solid',
                          borderColor: 'rgba(15, 23, 42, 0.08)',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: '#f1f5f9',
                            borderColor: 'rgba(15, 23, 42, 0.12)',
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                          <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 20, mt: 0.25 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                            {fileName}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 3.25 }}>
                          {file.type ? file.type : 'Unknown type'}{file.size ? ` • ${formatFileSize(file.size)}` : ''}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1, ml: 3.25 }}>
                          <Button
                            size="small"
                            startIcon={<DownloadIcon />}
                            component="a"
                            href={fullUrl}
                            target="_blank"
                            rel="noreferrer"
                            sx={{
                              borderRadius: 1.5,
                              fontWeight: 600,
                              px: 1.5
                            }}
                          >
                            Download
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleRemoveAttachment(idx)}
                            startIcon={<DeleteIcon fontSize="small" />}
                            sx={{
                              borderRadius: 1.5,
                              fontWeight: 600,
                              px: 1.5
                            }}
                          >
                            Hapus
                          </Button>
                        </Stack>
                      </Box>
                    </Fade>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={cardSx}>
            <CardHeader
              title="RINGKASAN"
              subheader="Cek ringkasan sebelum submit."
              {...cardHeaderProps}
            />
            <CardContent sx={{ pt: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5, p: 2, borderRadius: 2, bgcolor: completionPercent === 100 ? 'rgba(34, 197, 94, 0.1)' : 'rgba(15, 23, 42, 0.03)', border: '1px solid', borderColor: completionPercent === 100 ? 'rgba(34, 197, 94, 0.2)' : 'rgba(15, 23, 42, 0.08)' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  {completionPercent === 100 ? (
                    <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 24 }} />
                  ) : (
                    <ErrorIcon sx={{ color: completionPercent >= 60 ? '#f59e0b' : '#ef4444', fontSize: 24 }} />
                  )}
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                      {readinessLabel}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {completedChecklistCount}/{mandatoryChecklist.length} poin wajib ({completionPercent}%)
                    </Typography>
                  </Box>
                </Stack>
                <Chip
                  size="medium"
                  label={`${completionPercent}%`}
                  color={readinessColor}
                  sx={{ 
                    fontWeight: 800, 
                    fontSize: '1rem',
                    height: 32,
                    borderRadius: 1.5
                  }}
                />
              </Stack>

              {type !== FUND_TYPES.TRAVEL && (
                <>
                  <Typography variant="body2" sx={{ mt: 1.5 }}>TOTAL PERKIRAAN</Typography>
                  <Typography variant="h5" gutterBottom>Rp {Number(totalAmount || 0).toLocaleString('id-ID')}</Typography>
                </>
              )}
              <Stack spacing={0.5} mt={2} mb={2}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>DETAIL PERMINTAAN</Typography>
                <Stack spacing={0.8} sx={{ p: 2, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid rgba(15, 23, 42, 0.08)' }}>
                  <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>Tipe:</span>
                    <span style={{ fontWeight: 600 }}>{TYPE_LABELS[type] || type}</span>
                  </Typography>
                  {type === FUND_TYPES.GENERAL && (
                    <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#64748b' }}>Kategori:</span>
                      <span style={{ fontWeight: 600 }}>{primaryCategory || '-'}</span>
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>Project/HO:</span>
                    <span style={{ fontWeight: 600 }}>{selectedProjectLabel}</span>
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>Tanggal Pencairan:</span>
                    <span style={{ fontWeight: 600 }}>{plannedTransferDate || '-'}</span>
                  </Typography>
                  {type !== FUND_TYPES.TRAVEL && (
                    <>
                      <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b' }}>Penerima:</span>
                        <span style={{ fontWeight: 600 }}>{payee.payeeName || '-'}</span>
                      </Typography>
                      <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b' }}>Bank/Rek:</span>
                        <span style={{ fontWeight: 600 }}>{(payee.bankName || '')} {payee.accountNumber ? `(${payee.accountNumber})` : ''}</span>
                      </Typography>
                    </>
                  )}
                  <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>Lampiran:</span>
                    <span style={{ fontWeight: 600 }}>{attachments.length} file</span>
                  </Typography>
                </Stack>
              </Stack>
              <Stack spacing={0.6} sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid rgba(15, 23, 42, 0.08)' }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>CHECKLIST KELENGKAPAN</Typography>
                {mandatoryChecklist.map((item) => (
                  <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {item.done ? (
                        <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 18 }} />
                      ) : (
                        <ErrorIcon sx={{ color: '#ef4444', fontSize: 18 }} />
                      )}
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                        {item.label}
                      </Typography>
                    </Stack>
                    <Chip
                      size="small"
                      label={item.done ? 'SELESAI' : 'BELUM'}
                      color={item.done ? 'success' : 'error'}
                      variant={item.done ? 'filled' : 'outlined'}
                      sx={{ minWidth: 72, fontWeight: 700, borderRadius: 1.5 }}
                    />
                  </Box>
                ))}
              </Stack>
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Pengajuan maksimal pukul 12.00 WIB; lewat batas diproses hari berikutnya.
                </Typography>
              </Alert>
              <FormControlLabel
                control={<Checkbox checked={agreeTerms} onChange={e => setAgreeTerms(e.target.checked)} sx={{ '& .MuiSvgIcon-root': { fontSize: 24 } }} />}
                label="Saya membaca dan menyetujui ketentuan pengajuan dana (cut-off pukul 12.00 WIB dan jadwal pencairan)."
                sx={{ alignItems: 'flex-start', mt: 1, mb: 2 }}
              />
              <Stack direction="row" spacing={1}>
                <Button 
                  variant="outlined" 
                  startIcon={<SaveIcon />} 
                  onClick={() => onSubmit(false)} 
                  disabled={submitting}
                  sx={{
                    flex: 1,
                    px: 2.5,
                    py: 1.2,
                    borderRadius: 2,
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: 'rgba(15, 23, 42, 0.05)',
                    }
                  }}
                >
                  SIMPAN DRAFT
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={() => onSubmit(true)}
                  disabled={submitting || !agreeTerms}
                  sx={{
                    flex: 1,
                    px: 2.5,
                    py: 1.2,
                    borderRadius: 2,
                    fontWeight: 600,
                    bgcolor: '#0f172a',
                    '&:hover': {
                      bgcolor: '#0b1324',
                    }
                  }}
                >
                  SUBMIT
                </Button>
              </Stack>
            </CardContent>
          </Card>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FundRequestForm;
